import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSubscriptionStore } from '../stores/subscription-store';
import { useAuthStatus } from './use-auth';
import { getApiService } from '../api/services';
import { QUERY_KEYS } from '../types';
import { SubscriptionStatus, SubscriptionPlan } from '@lingugeek/schemas';

export function useSubscription() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuthStatus();
  const { subscription, isLoading, error, actions } = useSubscriptionStore();

  // Query for subscription data
  const subscriptionQuery = useQuery({
    queryKey: QUERY_KEYS.subscription,
    queryFn: async () => {
      const apiService = getApiService();
      const subscriptionData = await apiService.getSubscription();
      useSubscriptionStore.getState().actions.setSubscription(subscriptionData);
      return subscriptionData;
    },
    enabled: isAuthenticated,
  });

  // Load subscription mutation (for manual refresh)
  const loadSubscriptionMutation = useMutation({
    mutationFn: () => actions.loadSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });

  // Upgrade subscription mutation
  const upgradeSubscriptionMutation = useMutation({
    mutationFn: (plan: 'pro' | 'ultra') => actions.upgradeSubscription(plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });

  // Downgrade subscription mutation
  const downgradeSubscriptionMutation = useMutation({
    mutationFn: (plan: 'free' | 'pro') => actions.downgradeSubscription(plan),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => actions.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscription });
    },
  });

  // Computed values
  const isFreePlan = subscription?.plan === SubscriptionPlan.Free;
  const isProPlan = subscription?.plan === SubscriptionPlan.Pro;
  const isUltraPlan = subscription?.plan === SubscriptionPlan.Ultra;
  const isPremium = isProPlan || isUltraPlan;
  const isActive = subscription?.status === SubscriptionStatus.Active;
  const isCancelled = subscription?.status === SubscriptionStatus.Canceled;
  // Note: cancelAtPeriodEnd not available in current API response
  const willCancelAtPeriodEnd = false;

  return {
    // State
    subscription,
    isLoading: isLoading || subscriptionQuery.isLoading,
    error:
      error ||
      (subscriptionQuery.error instanceof Error
        ? subscriptionQuery.error.message
        : null),

    // Computed values
    isFreePlan,
    isProPlan,
    isUltraPlan,
    isPremium,
    isActive,
    isCancelled,
    willCancelAtPeriodEnd,

    // Actions
    loadSubscription: loadSubscriptionMutation.mutateAsync,
    upgradeSubscription: upgradeSubscriptionMutation.mutateAsync,
    downgradeSubscription: downgradeSubscriptionMutation.mutateAsync,
    cancelSubscription: cancelSubscriptionMutation.mutateAsync,

    // Mutation states
    isLoadingSubscription: loadSubscriptionMutation.isPending,
    isUpgrading: upgradeSubscriptionMutation.isPending,
    isDowngrading: downgradeSubscriptionMutation.isPending,
    isCancelling: cancelSubscriptionMutation.isPending,

    // Raw query/mutations for advanced use cases
    subscriptionQuery,
    loadSubscriptionMutation,
    upgradeSubscriptionMutation,
    downgradeSubscriptionMutation,
    cancelSubscriptionMutation,
  };
}

// Hook for subscription status only
export function useSubscriptionStatus() {
  const { subscription } = useSubscriptionStore();

  const isFreePlan = subscription?.plan === 'free';
  const isProPlan = subscription?.plan === 'pro';
  const isUltraPlan = subscription?.plan === 'ultra';
  const isPremium = isProPlan || isUltraPlan;
  const isActive = subscription?.status === 'active';

  return {
    plan: subscription?.plan || 'free',
    status: subscription?.status || 'expired',
    isFreePlan,
    isProPlan,
    isUltraPlan,
    isPremium,
    isActive,
  };
}
